import React, { useState, useCallback } from "react";
import { Form, Input, Select } from "semantic-ui-react";
import { useIntl } from "react-intl";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useDebounce } from "../../../libs/component_utils";
import { Config } from "../../../config/api";
import PropTypes from "prop-types";
import { CREATIVE_TYPES } from "./constants";

const FilterControl = ({ creative_type, filter, onChange, isVideo }) => {
	const intl = useIntl();
	const [titleValue, setTitleValue] = useState(filter.title);
	const isBrandLift = creative_type === CREATIVE_TYPES.brandLift;
	const isDisplayOrVideo = creative_type === "display" || creative_type === "video";

	const { whitelabelDoubleCreativeApproval, whitelabelBrandLiftCampaign } = useFlags();
	const filterTitle = useCallback((value) => {
		if (filter.title === value) return;
		filter.setTitle(value);
		onChange(filter.toJson());
	});

	const filterTitleDebounced = useDebounce(
		Config.search_debounce_delay,
		(value) => {
			if (value.length === 0 || value.length >= 3) {
				filterTitle(value);
			}
		}
	);

	const handleTitleChange = useCallback((e, { value }) => {
		setTitleValue(value);
		filterTitleDebounced(value);
	});

	const handleTitleKeyPress = useCallback((e) => {
		if (e.charCode === 13) {
			filterTitle(e.target.value);
		}
	});

  // This CreativeType is different than the creative_type prop (for display or video). That one should be called media_type and will be changed in the future.
  // This creativeType is the filter for creative types (offsite, onsite, instoreDOOH, brandLift) within the creative index page.
	const handleCreativeTypeChange = useCallback((e, { value }) => {
		filter.setCreativeType(value);
		onChange(filter.toJson());
	});

	const creativeTypeOptions = [
		{
			key: "all",
			value: "all",
			text: intl.formatMessage({ id: "LABEL_ALL_TYPES", defaultMessage: "All Types" }),
		},
		{
			key: CREATIVE_TYPES.offsite,
			value: CREATIVE_TYPES.offsite,
			text: intl.formatMessage({ id: "LABEL_CREATIVE_TYPE_OFF_SITE", defaultMessage: "Off-Site" }),
		},
		...(whitelabelDoubleCreativeApproval
			? [
				{
					key: CREATIVE_TYPES.onsite,
					value: CREATIVE_TYPES.onsite,
					text: intl.formatMessage({ id: "LABEL_CREATIVE_TYPE_ON_SITE", defaultMessage: "On-Site" }),
				},
				...(isVideo
					? [
						{
							key: CREATIVE_TYPES.instoreDOOH,
							value: CREATIVE_TYPES.instoreDOOH,
							text: intl.formatMessage({ id: "LABEL_CREATIVE_TYPE_INSTORE_DOOH", defaultMessage: "In-Store DOOH" }),
						},
					]
					: []),
			]
			: []),
		...(whitelabelBrandLiftCampaign
			? [
				{
					key: CREATIVE_TYPES.brandLift,
					value: CREATIVE_TYPES.brandLift,
					text: intl.formatMessage({ id: "LABEL_CREATIVE_TYPE_BRAND_LIFT", defaultMessage: "Brand Lift" }),
				},
			]
			: []),
	];

	const showCreativeTypeFilter = isDisplayOrVideo && whitelabelDoubleCreativeApproval;

	return (
		<Form autoComplete="off" noValidate size="tiny" style={{"marginTop": "15px"}}>
			<Form.Group>
				<Form.Field>
					<label>
						{isBrandLift &&
              intl.formatMessage({
                id: "LABEL_BRAND_LIFT_CREATIVES",
                defaultMessage: "Brand Lift Creatives",
              })}
            {isVideo &&
              intl.formatMessage({
                id: "LABEL_VIDEO_CREATIVES",
                defaultMessage: "Video Creatives",
              })}
            {!isBrandLift &&
              !isVideo &&
              intl.formatMessage({
                id: "LABEL_DISPLAY_CREATIVES",
                defaultMessage: "Display Creatives",
              })}
					</label>
					<Input
						icon={
							titleValue !== ""
								? {
									name: "x",
									className: "input-clear-icon",
									link: true,
									onClick: () => {
										setTitleValue("");
										filterTitle("");
									},
								}
								: "search"
						}
						iconPosition={titleValue !== "" ? null : "left"}
						placeholder={
							isVideo ? intl.formatMessage({
								id: "HINT_VIDEO_CREATIVES",
								defaultMessage: "Search video creatives by name",
							}) :
							intl.formatMessage({
								id: "HINT_DISPLAY_CREATIVES",
								defaultMessage: "Search display creatives by name",
							})
						}
						value={titleValue}
						onChange={handleTitleChange}
						onKeyPress={handleTitleKeyPress}
						name="title"
					/>
				</Form.Field>

				{showCreativeTypeFilter && (
					<Form.Field>
						<label>
							{intl.formatMessage({ id: "LABEL_CREATIVE_TYPE", defaultMessage: "Creative Type" })}
						</label>
						<Select
							options={creativeTypeOptions}
							value={filter.creativeType}
							onChange={handleCreativeTypeChange}
						/>
					</Form.Field>
				)}
			</Form.Group>
		</Form>
	);
};
FilterControl.propTypes = {
	"creative_type": PropTypes.string.isRequired,
	"filter": PropTypes.object.isRequired,
	"onChange": PropTypes.func.isRequired,
	"isVideo": PropTypes.bool.isRequired
};

export default FilterControl;
